import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Chart from "./ChartJsChart.js";
import { CHART_PALETTE, CHART_HEATMAP_COLORS, CHART_STATUS_COLORS } from "../utils/chartColors.js";
import axios from "axios";
import { Alert, Button, Card, Col, Dropdown, Form, InputGroup, Row, Table } from "react-bootstrap";
import Datetime from "react-datetime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faCalendar,
  faChartLine,
  faClock,
  faCog,
  faDownload,
  faEnvelope,
  faExclamationCircle,
  faFileExcel,
  faHandshake,
  faMagnifyingGlass,
  faQrcode,
  faRefresh,
  faUsers,
  faWifi,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import Loader from "./Loader.js";
import { ColumnSortingTable } from "./Table.js";
import NotyfContext from "../contexts/NotyfContext.js";
import { AnalyticsHelp, AnalyticsKpiCard as KpiCard } from "./AnalyticsKpiCard.js";
import { AnalyticsUsageLoadTestModal } from "./AnalyticsUsageLoadTestModal.js";

const DASHBOARD_TIMEZONE = "America/Sao_Paulo";
const AnalyticsTimezoneContext = React.createContext(DASHBOARD_TIMEZONE);
const DATE_PRESETS = [
  { key: "today", label: "Hoje" },
  { key: "7d", label: "7 dias" },
  { key: "30d", label: "30 dias" },
  { key: "event", label: "Evento (até 366 dias)" },
];

export const DASHBOARD_SECTIONS = [
  {
    key: "overview",
    label: "Visão geral",
    description: "Funil unificado de conversão e ritmo de atividade do evento.",
    apiSections: ["overview", "commercial"],
  },
  {
    key: "audience",
    label: "Público & Adesão",
    description: "Adesão ao webapp, retenção de participantes, tipos de usuário e ambiente.",
    apiSections: ["audience"],
  },
  {
    key: "journey",
    label: "Jornada & Navegação",
    description: "Origem de tráfego, campanhas UTM, páginas mais acessadas e tempo de permanência.",
    apiSections: ["acquisition", "navigation"],
  },
  {
    key: "business",
    label: "Conteúdo & Comercial",
    description: "Engajamento com agenda, palestrantes, expositores, patrocinadores e QR codes.",
    apiSections: ["content", "commercial"],
  },
  {
    key: "connections",
    label: "Networking & Mensageria",
    description: "Conexões entre participantes, convites e conversas, além de notificações, Web Push e e-mails.",
    apiSections: ["networking", "communications"],
  },
];

const DASHBOARD_API_SECTIONS = [...new Set(DASHBOARD_SECTIONS.flatMap((section) => section.apiSections))];

const LEGACY_SECTION_MAP = {
  overview: "overview",
  audience: "audience",
  acquisition: "journey",
  navigation: "journey",
  journey: "journey",
  content: "business",
  commercial: "business",
  business: "business",
  networking: "connections",
  communications: "connections",
  connections: "connections",
};

const SECTION_CSV_DATASETS = {
  overview: "participant_activity",
  audience: "participant_activity",
  journey: "routes",
  business: "sponsors",
  connections: "networking_pairs",
};

const EMPTY_PAYLOAD = { data: {} };

const QR_LABELS = {
  qrcode_lead_collected: "Leads coletados",
  qrcode_contact_scan: "Contatos trocados",
  qrcode_interest_point_scan: "Pontos de interesse",
  qrcode_exhibitor_scan: "Expositores escaneados",
};

const QR_ORIGIN_LABELS = {
  camera: "Câmera",
  deep_link: "Link/QR externo",
  profile_widget: "Perfil",
  unknown: "Desconhecida",
};

const PUSH_TYPE_LABELS = {
  admin_notification: "Comunicado",
  event_reminder: "Lembrete",
  stream_starting: "Transmissão",
  activity_available: "Atividade",
  chat_message: "Mensagem de chat",
  chat_invite_receive: "Convite de chat",
};

const ENTRY_SOURCE_LABELS = {
  direct: "Direto",
  utm: "Campanha UTM",
  qr_deep_link: "QR/deep link",
  embedded_stream: "Transmissão incorporada",
  pwa: "App/PWA",
  external_referrer: "Origem externa",
  internal_referrer: "Navegação interna",
  unknown: "Sem informação",
};

const PUSH_PERMISSION_LABELS = {
  granted: "Permitido",
  denied: "Negado",
  default: "Não decidido",
  unsupported: "Não suportado",
  unknown: "Sem informação",
};

const DISPLAY_MODE_LABELS = {
  browser: "Navegador",
  standalone: "Aberto como app",
  fullscreen: "Tela cheia",
  "minimal-ui": "Minimal UI",
  twa: "Trusted Web Activity",
  unknown: "Sem informação",
};

const DASHBOARD_EMPTY_TEXT = "Nenhum dado neste período";

function readAnalyticsQueryState(dataTable) {
  const fallbackRange = getPresetRange("event", dataTable);
  if (typeof window === "undefined") return { section: "overview", preset: "event", range: fallbackRange };
  const params = new URLSearchParams(window.location.search);
  const requestedSection = params.get("analytics_section");
  const mappedSection = LEGACY_SECTION_MAP[requestedSection] || "overview";
  const section = DASHBOARD_SECTIONS.some((item) => item.key === mappedSection) ? mappedSection : "overview";
  const requestedPreset = params.get("analytics_preset") || "event";
  const preset = requestedPreset === "custom" || DATE_PRESETS.some((item) => item.key === requestedPreset) ? requestedPreset : "event";
  const startDate = asDate(params.get("analytics_start"));
  const endDate = asDate(params.get("analytics_end"));
  const range = startDate && endDate && endDate > startDate ? { startDate, endDate } : getPresetRange(preset, dataTable);
  return { section, preset, range };
}

const SECTION_HELP = {
  overview: "Funil unificado de conversão de participantes e ritmo de sessões do evento.",
  audience: "Adoção do webapp, retenção, check-in e modo de abertura.",
  journey: "Origens de entrada, campanhas UTM, páginas acessadas e tempo de permanência.",
  business: "Interesse em agenda, palestrantes, expositores, patrocinadores e QR codes.",
  connections: "Convites, conexões de networking e entrega de notificações, Push e e-mails.",
};

const KPI_TOOLTIPS = {
  socketSessions: "Conta conexões do app, não visitas únicas. Recarregar, reconectar ou abrir outra aba pode gerar outra sessão.",
  users: "Registrados com alguma sessão ou ação no período. Visitantes sem login aparecem nas métricas de visitantes.",
  estimatedVisitors: "Estimativa por identificador anônimo persistente quando disponível; dados antigos caem para sessão de visitante.",
  qr: "QR válidos que geraram ação nova no app. Scans inválidos, repetidos ou já adicionados não entram aqui.",
  push: "Cliques no push e aberturas confirmadas do app. Não mede entrega, exibição pelo sistema operacional ou impressão visual.",
};

const TABLE_HELP = {
  users: "Métricas factuais por usuário registrado no período. A tabela não calcula score nem tenta medir qualidade de engajamento.",
  routes: "Páginas acessadas no webapp. URLs com parâmetros são agrupadas para evitar linhas duplicadas.",
  routeDuration: "Tempo visível registrado quando a pessoa sai de uma página. Dados antigos podem não ter esse tempo disponível.",
  routeSequences: "Jornadas recentes montadas pela ordem das rotas acessadas, sem mostrar IDs de sessão ou chaves técnicas.",
  environment: "Último estado observado de push, modo de abertura e dispositivo por registrado ou visitante estimado.",
  acquisition: "Sessões iniciadas por origem, campanha, origem externa ou rota inicial. Atribuição usa a URL no login do socket.",
  sponsor: "Cliques em links externos de patrocinadores e porcentagem de sessões que chegaram a clicar.",
  qr: "QR válidos que geraram resultado novo no app: leads, contatos, pontos de interesse e expositores.",
};

function toNumber(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function formatNumber(value) {
  return toNumber(value).toLocaleString("pt-BR");
}

function formatKpiNumber(value) {
  return formatNumber(value);
}

function formatPercentValue(value) {
  if (!hasValue(value) || !Number.isFinite(Number(value))) return "Sem base";
  return `${toNumber(value).toFixed(1).replace(".", ",")}%`;
}

function formatPercentWithBase(value, denominator) {
  return toNumber(denominator) > 0 ? formatPercentValue(value) : "Sem base";
}

function checkinUsageSubtitle(checkedInParticipants, adoptionRate) {
  return toNumber(checkedInParticipants) > 0
    ? `${formatPercentValue(adoptionRate)} de adesão ao webapp`
    : "Sem check-ins no período";
}

const ANALYTICS_ERROR_MESSAGES = {
  INTERNAL_ERROR: "Instabilidade temporária no servidor ao carregar esta seção. Tente novamente em instantes.",
  INVALID_EVENT: "Evento inválido ou não informado.",
  EVENT_NOT_FOUND: "Dados analíticos não encontrados para este evento.",
  FORBIDDEN: "Você não tem permissão para acessar os dados analíticos deste evento.",
  INSUFFICIENT_PERMISSIONS: "Permissão insuficiente para acessar os dados analíticos deste evento.",
  INVALID_SECTION: "Seção de analytics selecionada é inválida.",
  UNSUPPORTED_CONTRACT_VERSION: "Versão do contrato analítico não suportada.",
  INVALID_DATASET: "Conjunto de dados analíticos inválido.",
  INVALID_START_DATE: "Data inicial inválida.",
  INVALID_END_DATE: "Data final inválida.",
  INVALID_DATE_RANGE: "Período selecionado inválido. A data final deve ser posterior à data inicial.",
  DATE_RANGE_EXCEEDS_MAX: "O período máximo para consulta analítica é de 366 dias.",
  RANGE_PRECEDES_RESET: "O relatório foi reiniciado. Selecione um período posterior à data de corte.",
  NETWORK_ERROR: "Sem conexão com o servidor. Verifique sua internet e tente novamente.",
  TIMEOUT: "O tempo de resposta do servidor expirou. Verifique sua conexão e tente novamente.",
  UNAUTHORIZED: "Sua sessão expirou. Faça login novamente para continuar.",
};

export function formatAnalyticsErrorMessage(error, defaultMessage = "Erro ao carregar dados desta seção.") {
  if (!error) return defaultMessage;

  if (axios.isCancel && axios.isCancel(error)) return "";

  if (typeof error === "string") {
    if (ANALYTICS_ERROR_MESSAGES[error]) return ANALYTICS_ERROR_MESSAGES[error];
    const isTech = /(sql|syntax|database|column|table|select|from|where|join|unknown column|deadlock|connection|econnrefused|err_|cannot read property|is undefined|stack trace)/i.test(error);
    return isTech ? ANALYTICS_ERROR_MESSAGES.INTERNAL_ERROR : error;
  }

  if (error.code === "ECONNABORTED" || /timeout/i.test(error.message || "")) {
    return ANALYTICS_ERROR_MESSAGES.TIMEOUT;
  }

  if (!error.response && (error.request || /network\s*error/i.test(error.message || ""))) {
    return ANALYTICS_ERROR_MESSAGES.NETWORK_ERROR;
  }

  const code = String(error.response?.data?.code || error.code || "").trim();
  if (code && ANALYTICS_ERROR_MESSAGES[code]) {
    return ANALYTICS_ERROR_MESSAGES[code];
  }

  const status = error.response?.status;
  if (status === 401) {
    return ANALYTICS_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 403) {
    return ANALYTICS_ERROR_MESSAGES.FORBIDDEN;
  }
  if (status === 404) {
    return ANALYTICS_ERROR_MESSAGES.EVENT_NOT_FOUND;
  }
  if (status >= 500) {
    return ANALYTICS_ERROR_MESSAGES.INTERNAL_ERROR;
  }

  const rawMessage = error.response?.data?.message || (typeof error.message === "string" ? error.message : "");
  const isTechnicalError =
    typeof rawMessage === "string" &&
    /(sql|syntax|database|column|table|select|from|where|join|unknown column|deadlock|connection|econnrefused|err_|cannot read property|is undefined|stack trace|at line)/i.test(rawMessage);

  if (rawMessage && !isTechnicalError && !/^[A-Z_]+$/.test(rawMessage)) {
    return rawMessage;
  }

  return defaultMessage;
}

function formatSeconds(value) {
  if (!hasValue(value) || !Number.isFinite(Number(value))) return "-";
  const seconds = Math.max(0, Math.round(toNumber(value)));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;

  if (days > 0) return `${days}d ${String(hours).padStart(2, "0")}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  if (minutes > 0) return `${minutes}m ${String(rest).padStart(2, "0")}s`;
  return `${rest}s`;
}

function parseUtcDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  if (!raw) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const date = new Date(hasZone ? normalized : `${normalized}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value, timezone = DASHBOARD_TIMEZONE) {
  const date = parseUtcDate(value);
  if (!date) return value ? String(value) : "";
  return formatInTimeZone(date, timezone, "dd/MM/yyyy HH:mm");
}

function formatDateOnly(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return format(new Date(year, month - 1, day), "dd/MM");
  }
  const date = parseUtcDate(value);
  if (!date) return String(value).slice(0, 10);
  return formatInTimeZone(date, DASHBOARD_TIMEZONE, "dd/MM");
}

function asDate(value, timezone = DASHBOARD_TIMEZONE) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  if (!raw) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const brazilianDate = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/,
  );
  if (brazilianDate) {
    const [, day, month, year, hour = "00", minute = "00", second = "00"] = brazilianDate;
    const date = zonedTimeToUtc(`${year}-${month}-${day} ${hour}:${minute}:${second}`, timezone);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const normalized = raw.includes("T") ? raw.replace("T", " ") : raw;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(normalized);
  const date = hasZone
    ? new Date(raw.includes("T") ? raw : raw.replace(" ", "T"))
    : zonedTimeToUtc(dateOnly ? `${normalized} 00:00:00` : normalized, timezone);
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeDashboardRange(range, timezone = DASHBOARD_TIMEZONE) {
  return {
    startDate: asDate(range?.startDate, timezone),
    endDate: asDate(range?.endDate, timezone),
  };
}

function addPlainDateDays(value, days) {
  const parts = String(value || "").split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return null;
  const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  date.setUTCDate(date.getUTCDate() + days);
  return formatInTimeZone(date, "UTC", "yyyy-MM-dd");
}

function getDashboardDateString(value = new Date(), timezone = DASHBOARD_TIMEZONE) {
  return formatInTimeZone(value, timezone, "yyyy-MM-dd");
}

function getDashboardDayStartUtc(dateString, timezone = DASHBOARD_TIMEZONE) {
  return zonedTimeToUtc(`${dateString} 00:00:00`, timezone);
}

function getDashboardDayRange(days, timezone = DASHBOARD_TIMEZONE) {
  const today = getDashboardDateString(new Date(), timezone);
  const startDateString = addPlainDateDays(today, -(days - 1));
  const endDateString = addPlainDateDays(today, 1);
  return {
    startDate: getDashboardDayStartUtc(startDateString, timezone),
    endDate: getDashboardDayStartUtc(endDateString, timezone),
  };
}

function formatPickerDate(value, timezone = DASHBOARD_TIMEZONE) {
  if (!value) return "";
  const parsedDate = asDate(value, timezone);
  return parsedDate
    ? formatInTimeZone(parsedDate, timezone, "dd/MM/yyyy HH:mm")
    : String(value);
}

function pickerDateToDashboardUtc(value, timezone = DASHBOARD_TIMEZONE) {
  if (typeof value === "string") {
    return asDate(value, timezone) || value;
  }
  if (!value || typeof value !== "object" || value._isValid === false) return null;
  const formattedValue = typeof value.format === "function"
    ? value.format("YYYY-MM-DD HH:mm:ss")
    : format(value.toDate ? value.toDate() : value, "yyyy-MM-dd HH:mm:ss");
  return zonedTimeToUtc(formattedValue, timezone);
}

function safeRows(rows) {
  return Array.isArray(rows) ? rows : [];
}

function labelUnknown(value, fallback = "Sem informação") {
  if (!hasValue(value)) return fallback;
  const stringValue = String(value).trim();
  const normalizedValue = stringValue.toLowerCase();
  if (!stringValue || normalizedValue === "unknown" || normalizedValue === "null" || normalizedValue === "undefined") return fallback;
  return stringValue;
}

function labelFromMap(map, value, fallback = "Sem informação") {
  const key = labelUnknown(value, "").toLowerCase();
  if (!key) return fallback;
  return map[key] || value || fallback;
}

function booleanLabel(value) {
  const stringValue = String(value).toLowerCase();
  if (stringValue === "true" || stringValue === "1") return "Sim";
  if (stringValue === "false" || stringValue === "0") return "Não";
  return labelUnknown(value);
}

function labelQrEventName(value) {
  return QR_LABELS[value] || labelUnknown(value);
}

function labelQrOrigin(value) {
  return labelFromMap(QR_ORIGIN_LABELS, value);
}

function labelPushType(value) {
  return PUSH_TYPE_LABELS[value] || value || "Notificação";
}

function labelEntrySource(value) {
  return labelFromMap(ENTRY_SOURCE_LABELS, value);
}

function labelPushPermission(value) {
  return labelFromMap(PUSH_PERMISSION_LABELS, value);
}

function labelDisplayMode(value) {
  return labelFromMap(DISPLAY_MODE_LABELS, value);
}

function labelRoutePath(value) {
  const label = labelUnknown(value, "Sem rota");
  return label.toLowerCase() === "sem rota" ? "Sem rota" : label;
}

function labelCampaign(row = {}) {
  const source = labelUnknown(row.utm_source, "");
  const medium = labelUnknown(row.utm_medium, "");
  const campaign = labelUnknown(row.utm_campaign, "");
  const parts = [source, medium, campaign].filter(Boolean);
  return parts.length ? parts.join(" / ") : "Sem campanha";
}

function getEventRange(dataTable) {
  const timezone = dataTable?.timezone || DASHBOARD_TIMEZONE;
  const startDate = asDate(dataTable?.creation_date, timezone);
  const endDate = asDate(dataTable?.end_date, timezone);
  const fallbackRange = getDashboardDayRange(30, timezone);
  const resolvedStartDate = startDate || fallbackRange.startDate;
  const resolvedEndDate = endDate && endDate > resolvedStartDate ? endDate : fallbackRange.endDate;

  if (resolvedEndDate <= resolvedStartDate) return fallbackRange;

  const maxRangeMs = 366 * 24 * 60 * 60 * 1000;
  if ((resolvedEndDate.getTime() - resolvedStartDate.getTime()) > maxRangeMs) {
    return { startDate: new Date(resolvedEndDate.getTime() - maxRangeMs), endDate: resolvedEndDate };
  }

  return {
    startDate: resolvedStartDate,
    endDate: resolvedEndDate,
  };
}

function getPresetRange(preset, dataTable) {
  const timezone = dataTable?.timezone || DASHBOARD_TIMEZONE;
  if (preset === "today") return getDashboardDayRange(1, timezone);
  if (preset === "7d") return getDashboardDayRange(7, timezone);
  if (preset === "30d") return getDashboardDayRange(30, timezone);
  return getEventRange(dataTable);
}

function getRangeKey(range) {
  if (!range?.startDate || !range?.endDate) return "";
  return `${range.startDate.toISOString()}|${range.endDate.toISOString()}`;
}

function getAvailableAnalyticsRange(range, minimumDate) {
  if (!minimumDate || !range?.startDate || !range?.endDate || range.startDate >= minimumDate) return range;
  return {
    startDate: minimumDate,
    endDate: range.endDate > minimumDate ? range.endDate : new Date(minimumDate.getTime() + 24 * 60 * 60 * 1000),
  };
}

function getRangeValidationMessage(range, minimumDate, timezone = DASHBOARD_TIMEZONE) {
  if (!range?.startDate || !range?.endDate) return "Informe início e fim.";
  if (range.endDate <= range.startDate) return "O fim precisa ser depois do início.";
  if (minimumDate && range.startDate < minimumDate) return `Os dados analíticos estão disponíveis a partir de ${formatDateTime(minimumDate, timezone)}.`;
  if ((range.endDate.getTime() - range.startDate.getTime()) > (366 * 24 * 60 * 60 * 1000)) return "O período máximo para analytics é de 366 dias.";
  return "";
}

function isSameRange(firstRange, secondRange) {
  return getRangeKey(firstRange) === getRangeKey(secondRange);
}

function TitleWithHelp({ as: Component = "h6", className = "mb-1", children, tooltip }) {
  return (
    <Component className={className}>
      {children}
      <AnalyticsHelp text={tooltip} />
    </Component>
  );
}

function SimpleList({ rows, labelKey, valueKey, emptyText, valueFormatter = formatNumber, labelFormatter }) {
  const data = safeRows(rows).slice(0, 8);
  if (data.length === 0) return <p className="text-muted mb-0">{emptyText || DASHBOARD_EMPTY_TEXT}</p>;

  return (
    <Table size="sm" responsive className="mb-0">
      <tbody>
        {data.map((row, index) => (
          <tr key={`${labelKey}-${index}`}>
            <td className="ps-0">{labelFormatter ? labelFormatter(row[labelKey], row) : labelUnknown(row[labelKey])}</td>
            <td className="text-end pe-0 fw-semibold">{valueFormatter(row[valueKey])}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

const numberCell = ({ value }) => formatNumber(value);
const secondsCell = ({ value }) => formatSeconds(value);
const percentCell = ({ value, row }) => (row?.original?.socket_sessions !== undefined ? formatPercentWithBase(value, row.original.socket_sessions) : formatPercentValue(value));
const dateCell = ({ value }) => formatDateTime(value);
const booleanCell = ({ value }) => booleanLabel(value);
const routeCell = ({ value }) => labelRoutePath(value);

function DashboardTable({
  title,
  description,
  columns,
  data,
  exportTitle,
  tooltip,
  extraHeader,
  onDownloadCsv,
  downloadingCsv = false,
}) {
  const [triggerExport, setTriggerExport] = useState(false);
  const timezone = useContext(AnalyticsTimezoneContext);
  const tableRows = safeRows(data);
  const columnsWithTooltips = useMemo(
    () => safeRows(columns).map((column) => {
      const nextColumn = {
        ...column,
        tooltip: column.tooltip || tooltip || description,
      };
      if (column.Cell === dateCell) {
        nextColumn.Cell = ({ value }) => formatDateTime(value, timezone);
      } else if (column.Cell) {
        nextColumn.Cell = column.Cell;
      } else {
        delete nextColumn.Cell;
      }
      return nextColumn;
    }),
    [columns, tooltip, description, timezone],
  );

  return (
    <div className="pb-2">
      {(title || extraHeader || onDownloadCsv) && (
        <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
          {title ? (
            <div>
              <TitleWithHelp tooltip={tooltip || description}>{title}</TitleWithHelp>
              {description && <p className="text-muted small mb-0">{description}</p>}
            </div>
          ) : <div />}
          <div className="d-flex gap-2 align-items-center">
            {extraHeader}
            {onDownloadCsv && (
              <Button type="button" variant="outline-primary" size="sm" onClick={onDownloadCsv} title="Baixar CSV completo" disabled={downloadingCsv}>
                {downloadingCsv ? "Preparando CSV..." : "CSV completo"}
              </Button>
            )}
            <Button type="button" variant="outline-primary" size="sm" onClick={() => setTriggerExport(true)} title="Exportar tabela atual" disabled={tableRows.length === 0}>
              <FontAwesomeIcon icon={faDownload} />
            </Button>
          </div>
        </div>
      )}
      {tableRows.length > 0 ? (
        <ColumnSortingTable
          columns={columnsWithTooltips}
          data={tableRows}
          title={exportTitle || title}
          triggerExport={triggerExport}
          setTriggerExport={setTriggerExport}
          getCellProps={() => ({})}
          tableCountTooltip="Quantidade de linhas desta tabela para o período selecionado."
        />
      ) : (
        <Card className="border-0 bg-light">
          <Card.Body>
            <p className="text-muted text-center mb-0">{DASHBOARD_EMPTY_TEXT}</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

function SectionShell({ loading, error, onRetry, children }) {
  if (loading) {
    return (
      <div className="py-5">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mb-3">
        <Card.Body className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
          <div>
            <h6 className="mb-1 text-danger">Não foi possível carregar esta seção</h6>
            <p className="text-muted small mb-0">{error}</p>
          </div>
          <Button type="button" variant="outline-primary" onClick={onRetry}>
            Tentar novamente
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return children;
}

const baseChartOptions = {
  chart: { toolbar: { show: true }, zoom: { enabled: false } },
  dataLabels: { enabled: false },
  stroke: { width: 3, curve: "smooth" },
  grid: { borderColor: "#f1f1f1" },
  legend: { position: "top" },
};

function buildTrendChart(rows, series, colors = CHART_PALETTE) {
  const categories = safeRows(rows).map((row) => formatDateOnly(row.metric_date));
  const maximumVisibleLabels = categories.length > 90 ? 6 : categories.length > 31 ? 8 : 10;
  const labelInterval = Math.max(1, Math.ceil((categories.length - 1) / Math.max(1, maximumVisibleLabels - 1)));
  const axisCategories = categories.map((label, index) => (
    index === 0 || index === categories.length - 1 || index % labelInterval === 0 ? label : ""
  ));
  return {
    options: {
      ...baseChartOptions,
      xaxis: {
        categories: axisCategories,
        labels: { hideOverlappingLabels: true, rotate: categories.length > 31 ? -45 : 0 },
      },
      yaxis: { labels: { formatter: (value) => Math.round(value) } },
      colors,
    },
    series,
  };
}

function hasChartData(rows, keys) {
  return safeRows(rows).some((row) => keys.some((key) => toNumber(typeof key === "function" ? key(row) : row[key]) > 0));
}

function topPositiveRows(rows, valueGetter, limit = 8, preserveOrder = false) {
  const filteredRows = safeRows(rows).filter((row) => toNumber(valueGetter(row)) > 0);
  const orderedRows = preserveOrder
    ? filteredRows
    : filteredRows.sort((a, b) => toNumber(valueGetter(b)) - toNumber(valueGetter(a)));
  return orderedRows.slice(0, limit);
}

function ChartEmpty({ text = DASHBOARD_EMPTY_TEXT }) {
  return <p className="text-muted my-5 text-center">{text}</p>;
}

function EmptySectionState({ text }) {
  return (
    <Card className="mb-3 mx-2">
      <Card.Body>
        <p className="text-muted mb-0">{text}</p>
      </Card.Body>
    </Card>
  );
}

function ToggleableChartCard({
  title,
  tooltip,
  children,
  tableData,
  tableColumns,
  tableTitle,
  xl = 6,
  defaultView = "chart",
  extraHeader,
}) {
  const [view, setView] = useState(defaultView);
  const hasTable = Boolean(tableData && tableColumns);

  return (
    <Col xs={12} xl={xl} className="mb-3">
      <Card className="h-100">
        <Card.Body className="d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <TitleWithHelp className="mb-0" tooltip={tooltip}>{title}</TitleWithHelp>
            <div className="d-flex align-items-center gap-2">
              {extraHeader}
              {hasTable && (
                <div className="btn-group btn-group-sm" role="group" aria-label="Visualização">
                  <Button
                    type="button"
                    variant={view === "chart" ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => setView("chart")}
                    className="py-0 px-2"
                  >
                    Gráfico
                  </Button>
                  <Button
                    type="button"
                    variant={view === "table" ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => setView("table")}
                    className="py-0 px-2"
                  >
                    Tabela
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="flex-grow-1">
            {view === "chart" || !hasTable ? (
              children
            ) : (
              <DashboardTable
                title=""
                columns={tableColumns}
                data={tableData}
                exportTitle={tableTitle || title}
              />
            )}
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
}

function buildBarChart(rows, {
  labelGetter,
  valueGetter,
  name = "Total",
  horizontal = true,
  colors = CHART_PALETTE,
  valueFormatter = formatNumber,
  colorGetter,
  limit = 8,
  preserveOrder = false,
}) {
  const data = topPositiveRows(rows, valueGetter, limit, preserveOrder);
  return {
    rows: data,
    options: {
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, toolbar: { show: false } },
      stroke: { width: 0 },
      plotOptions: { bar: { horizontal, borderRadius: 3, barHeight: "70%", distributed: Boolean(colorGetter) } },
      xaxis: horizontal
        ? { categories: data.map(labelGetter), labels: { formatter: (value) => valueFormatter(value) } }
        : { categories: data.map(labelGetter) },
      yaxis: horizontal ? {} : { labels: { formatter: (value) => formatNumber(value) } },
      tooltip: { y: { formatter: (value) => valueFormatter(value) } },
      colors: colorGetter ? data.map(colorGetter) : colors,
    },
    series: [{ name, data: data.map((row) => toNumber(valueGetter(row))) }],
  };
}

function buildDonutChart(rows, {
  labelGetter,
  valueGetter,
  colors = CHART_PALETTE,
  limit = 8,
}) {
  const data = topPositiveRows(rows, valueGetter, limit);
  return {
    rows: data,
    options: {
      chart: { toolbar: { show: false } },
      labels: data.map(labelGetter),
      legend: { position: "bottom" },
      dataLabels: { enabled: true },
      tooltip: { y: { formatter: (value) => formatNumber(value) } },
      colors,
    },
    series: data.map((row) => toNumber(valueGetter(row))),
  };
}

function buildGroupedBarChart(rows, seriesDefs, {
  categoryGetter,
  colors = CHART_PALETTE,
  horizontal = false,
  stacked = false,
  valueFormatter = formatNumber,
  limit,
}) {
  const data = limit ? safeRows(rows).slice(0, limit) : safeRows(rows);
  return {
    rows: data,
    options: {
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, stacked, toolbar: { show: false } },
      stroke: { width: 0 },
      plotOptions: { bar: { horizontal, borderRadius: 3 } },
      xaxis: { categories: data.map(categoryGetter), labels: { formatter: (value) => (horizontal ? valueFormatter(value) : value) } },
      yaxis: { labels: { formatter: (value) => (horizontal ? value : formatNumber(value)) } },
      tooltip: { y: { formatter: (value) => valueFormatter(value) } },
      colors,
    },
    series: seriesDefs.map((definition) => ({
      name: definition.name,
      data: data.map((row) => toNumber(definition.valueGetter(row))),
    })),
  };
}

function buildFunnelChart(rows, {
  labelGetter = (row) => row.label,
  valueGetter = (row) => row.value,
  colors = CHART_PALETTE,
}) {
  const data = safeRows(rows).filter((row) => toNumber(valueGetter(row)) >= 0);
  return {
    rows: data,
    options: {
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, toolbar: { show: false } },
      stroke: { width: 0 },
      dataLabels: {
        enabled: true,
        formatter: (value) => formatNumber(value),
        style: { colors: ["#fff"] },
      },
      plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 3, barHeight: "70%" } },
      xaxis: { categories: data.map(labelGetter), labels: { formatter: (value) => formatNumber(value) } },
      legend: { show: false },
      tooltip: { y: { formatter: (value) => formatNumber(value) } },
      colors,
    },
    series: [{ name: "Total", data: data.map((row) => toNumber(valueGetter(row))) }],
  };
}

const WEEKDAY_LABELS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sab", "Dom"];

function buildHeatmapChart(rows) {
  const data = safeRows(rows);
  const values = new Map(data.map((row) => [`${toNumber(row.weekday_index)}:${toNumber(row.hour_of_day)}`, toNumber(row.activity_count)]));
  const maxValue = data.reduce((maximum, row) => Math.max(maximum, toNumber(row.activity_count)), 0);
  const high = Math.max(1, maxValue);
  const medium = Math.max(1, Math.ceil(high * 0.66));
  const low = Math.max(1, Math.ceil(high * 0.33));
  const colorRanges = [
    { from: 0, to: 0, color: CHART_HEATMAP_COLORS.empty, name: "Sem atividade" },
    { from: 1, to: low, color: CHART_HEATMAP_COLORS.low, name: "Baixa" },
  ];
  if (medium > low) colorRanges.push({ from: low + 1, to: medium, color: CHART_HEATMAP_COLORS.medium, name: "Média" });
  if (high > medium) colorRanges.push({ from: medium + 1, to: high, color: CHART_HEATMAP_COLORS.high, name: "Alta" });
  return {
    rows: data,
    options: {
      chart: { toolbar: { show: false } },
      dataLabels: { enabled: false },
      plotOptions: {
        heatmap: {
          radius: 2,
          colorScale: { ranges: colorRanges },
        },
      },
      xaxis: { type: "category", categories: Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, "0")}h`) },
      tooltip: { y: { formatter: (value) => `${formatNumber(value)} atividades` } },
      legend: { position: "top" },
    },
    series: WEEKDAY_LABELS.map((name, weekday) => ({
      name,
      data: Array.from({ length: 24 }, (_, hour) => ({ x: `${String(hour).padStart(2, "0")}h`, y: values.get(`${weekday}:${hour}`) || 0 })),
    })),
  };
}



const columnSets = {
  users: [
    { Header: "Usuário", accessor: "email", Cell: ({ value, row }) => value || `Usuário ${row.original.user_id}` },
    { Header: "Tipo", accessor: "type_label" },
    { Header: "Sessões", accessor: "socket_sessions", Cell: numberCell },
    { Header: "Tempo conectado", accessor: "connected_seconds", Cell: secondsCell },
    { Header: "Dias ativos", accessor: "active_days", Cell: numberCell },
    { Header: "Rotas", accessor: "route_accesses", Cell: numberCell },
    { Header: "QR", accessor: "qr_actions", Cell: numberCell },
    { Header: "Patrocinadores", accessor: "sponsor_clicks", Cell: numberCell },
    { Header: "Push cliques", accessor: "webpush_clicks", Cell: numberCell },
    { Header: "Push aberturas", accessor: "webpush_opens", Cell: numberCell },
    { Header: "Última ação", accessor: "last_action_at", Cell: dateCell },
  ],
  routes: [
    { Header: "Rota", accessor: "route_path", Cell: routeCell },
    { Header: "Acessos", accessor: "access_count", Cell: numberCell },
    { Header: "Participantes identificados", accessor: "registered_users", Cell: numberCell },
    { Header: "Visitantes estimados", accessor: "guest_devices", Cell: numberCell },
    { Header: "Sessões de visitantes", accessor: "guest_sessions", Cell: numberCell },
    { Header: "Sessões no webapp", accessor: "sessions", Cell: numberCell },
    { Header: "Primeiro acesso", accessor: "first_seen_at", Cell: dateCell },
    { Header: "Último acesso", accessor: "last_seen_at", Cell: dateCell },
  ],
  routeDuration: [
    { Header: "Rota", accessor: "route_path", Cell: routeCell },
    { Header: "Saídas", accessor: "leave_count", Cell: numberCell },
    { Header: "Tempo visível", accessor: "duration_seconds", Cell: secondsCell },
    { Header: "Média", accessor: "avg_duration_seconds", Cell: secondsCell },
    { Header: "Maior", accessor: "max_duration_seconds", Cell: secondsCell },
    { Header: "Visitantes estimados", accessor: "guest_devices", Cell: numberCell },
    { Header: "Sessões de visitantes", accessor: "guest_sessions", Cell: numberCell },
    { Header: "Registrados", accessor: "registered_users", Cell: numberCell },
  ],
  routeSequences: [
    { Header: "Usuário", accessor: "user_label" },
    { Header: "Tipo", accessor: "user_type_label" },
    { Header: "Rotas", accessor: "route_count", Cell: numberCell },
    { Header: "Primeiro", accessor: "first_seen_at", Cell: dateCell },
    { Header: "Último", accessor: "last_seen_at", Cell: dateCell },
    { Header: "Sequência", accessor: "route_sequence" },
  ],
  typeDistribution: [
    { Header: "Tipo", accessor: "type_label" },
    { Header: "Registrados", accessor: "users", Cell: numberCell },
  ],
  retention: [
    { Header: "Dias ativos", accessor: "active_days", Cell: numberCell },
    { Header: "Registrados", accessor: "users", Cell: numberCell },
  ],
  environment: [
    { Header: "Permissão push", accessor: "push_permission", Cell: ({ value }) => labelPushPermission(value) },
    { Header: "Assinatura push", accessor: "has_push_subscription", Cell: booleanCell },
    { Header: "Modo de abertura", accessor: "display_mode", Cell: ({ value }) => labelDisplayMode(value) },
    { Header: "Aberto como app", accessor: "is_standalone", Cell: booleanCell },
    { Header: "OS", accessor: "os" },
    { Header: "Mobile", accessor: "is_mobile", Cell: booleanCell },
    { Header: "Registrados", accessor: "users", Cell: numberCell },
  ],
  entrySources: [
    { Header: "Origem", accessor: "entry_source", Cell: ({ value }) => labelEntrySource(value) },
    { Header: "Sessões", accessor: "socket_sessions", Cell: numberCell },
    { Header: "Registrados", accessor: "registered_users", Cell: numberCell },
    { Header: "Visitantes estimados", accessor: "guest_devices", Cell: numberCell },
    { Header: "Sessões de visitantes", accessor: "guest_sessions", Cell: numberCell },
  ],
  campaigns: [
    { Header: "UTM source", accessor: "utm_source" },
    { Header: "UTM medium", accessor: "utm_medium" },
    { Header: "Campanha", accessor: "utm_campaign" },
    { Header: "Sessões", accessor: "socket_sessions", Cell: numberCell },
    { Header: "Registrados", accessor: "registered_users", Cell: numberCell },
    { Header: "Visitantes estimados", accessor: "guest_devices", Cell: numberCell },
  ],
  sponsorClicks: [
    { Header: "Patrocinador", accessor: "sponsor_name" },
    { Header: "Tier", accessor: "sponsor_tier" },
    { Header: "Impressões", accessor: "impression_count", Cell: numberCell },
    { Header: "Visualizadores únicos", accessor: "unique_viewer_sessions", Cell: numberCell },
    { Header: "Detalhes", accessor: "detail_open_count", Cell: numberCell },
    { Header: "Cliques", accessor: "click_count", Cell: numberCell },
    { Header: "CTR", accessor: "sponsor_ctr", Cell: percentCell },
  ],
  qrSummary: [
    { Header: "Tipo", accessor: "event_name", Cell: ({ value }) => labelQrEventName(value) },
    { Header: "Item", accessor: "entity_label" },
    { Header: "QR válidos", accessor: "scan_count", Cell: numberCell },
    { Header: "Registrados", accessor: "registered_users", Cell: numberCell },
    { Header: "Visitantes estimados", accessor: "guest_devices", Cell: numberCell },
    { Header: "Sessões de visitantes", accessor: "guest_sessions", Cell: numberCell },
    { Header: "Último", accessor: "last_scanned_at", Cell: dateCell },
  ],
  leadsByExhibitor: [
    { Header: "Expositor", accessor: "exhibitor_name" },
    { Header: "Leads", accessor: "leads_collected", Cell: numberCell },
    { Header: "Leads únicos", accessor: "unique_lead_users", Cell: numberCell },
    { Header: "Coletores", accessor: "collector_users", Cell: numberCell },
    { Header: "Último", accessor: "last_collected_at", Cell: dateCell },
  ],
  networkingPairs: [
    { Header: "Participante 1", accessor: "participant_1" },
    { Header: "Participante 2", accessor: "participant_2" },
    { Header: "Resultados", accessor: "outcomes" },
    { Header: "Primeiro resultado", accessor: "first_outcome_at", Cell: dateCell },
    { Header: "Mais recente", accessor: "last_outcome_at", Cell: dateCell },
  ],
  exhibitorEngagement: [
    { Header: "Expositor", accessor: "exhibitor_name" },
    { Header: "Impressões", accessor: "impressions", Cell: numberCell },
    { Header: "Visualizadores únicos", accessor: "unique_viewer_sessions", Cell: numberCell },
    { Header: "Detalhes", accessor: "detail_opens", Cell: numberCell },
    { Header: "Favoritos atuais", accessor: "current_favorites", Cell: numberCell },
  ],
  sourceConversion: [
    { Header: "Origem", accessor: "entry_source", Cell: ({ value }) => labelEntrySource(value) },
    { Header: "Base de sessões", accessor: "denominator_sessions", Cell: numberCell },
    { Header: "Sessões ativadas", accessor: "activated_sessions", Cell: numberCell },
    { Header: "Ativação", accessor: "activation_rate", Cell: percentCell },
    { Header: "Sessões com networking", accessor: "networking_sessions", Cell: numberCell },
    { Header: "Conversão networking", accessor: "networking_conversion_rate", Cell: percentCell },
    { Header: "Sessões com patrocinador", accessor: "sponsor_sessions", Cell: numberCell },
    { Header: "Conversão patrocinador", accessor: "sponsor_conversion_rate", Cell: percentCell },
  ],
  connectionDistribution: [
    { Header: "Conexões", accessor: "connection_count", Cell: numberCell },
    { Header: "Participantes", accessor: "participants", Cell: numberCell },
  ],
  contentSchedule: [
    { Header: "Conteúdo", accessor: "title" },
    { Header: "Aberturas", accessor: "detail_opens", Cell: numberCell },
    { Header: "Interesses no período", accessor: "period_interests", Cell: numberCell },
    { Header: "Confirmações no período", accessor: "period_confirmations", Cell: numberCell },
    { Header: "Interesses atuais", accessor: "current_interests", Cell: numberCell },
    { Header: "Confirmações atuais", accessor: "current_confirmations", Cell: numberCell },
  ],
  notificationTypes: [
    { Header: "Tipo", accessor: "notification_type" },
    { Header: "Criadas", accessor: "created", Cell: numberCell },
    { Header: "Visualizadas", accessor: "viewed", Cell: numberCell },
    { Header: "Lidas", accessor: "read_count", Cell: numberCell },
  ],
};

function buildAudienceEnvironmentRows(data) {
  const grouped = new Map();
  const ensureRow = (label, permission) => {
    if (!grouped.has(label)) grouped.set(label, { label, permission, registered: 0, guests: 0, total: 0 });
    return grouped.get(label);
  };

  safeRows(data?.environment_distribution).forEach((row) => {
    const modeRow = ensureRow(`Modo: ${labelDisplayMode(row.display_mode)}`);
    modeRow.registered += toNumber(row.users);
    modeRow.total += toNumber(row.users);

    const pushRow = ensureRow(`Push: ${labelPushPermission(row.push_permission)}`, row.push_permission);
    pushRow.registered += toNumber(row.users);
    pushRow.total += toNumber(row.users);
  });

  safeRows(data?.environment_guest_distribution).forEach((row) => {
    const guestCount = toNumber(row.guest_devices);
    const modeRow = ensureRow(`Modo: ${labelDisplayMode(row.display_mode)}`);
    modeRow.guests += guestCount;
    modeRow.total += guestCount;

    const pushRow = ensureRow(`Push: ${labelPushPermission(row.push_permission)}`, row.push_permission);
    pushRow.guests += guestCount;
    pushRow.total += guestCount;
  });

  return Array.from(grouped.values())
    .filter((row) => row.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);
}

// -----------------------------------------------------------------------------
// SECTION 1: VISÃO GERAL (FUNIL UNIFICADO DE CONVERSÃO)
// -----------------------------------------------------------------------------
function UnifiedOverviewSection({ data, commercialData = {} }) {
  const overview = data?.overview || data || {};
  const daily = safeRows(data?.daily);
  const commOverview = commercialData?.overview || {};

  const commercialActionsCount = toNumber(overview.sponsor_clicks)
    + toNumber(overview.qrcode_leads)
    + toNumber(commOverview.sponsor_clicks)
    + toNumber(commOverview.unique_exhibitor_leads);

  const fullConversionFunnelRows = [
    { label: "1. Cadastrados no evento", value: overview.eligible_participants },
    { label: "2. Presença (Check-in)", value: overview.checked_in_participants },
    { label: "3. Acesso ao Webapp", value: overview.activated_participants },
    { label: "4. Networking Ativo", value: overview.connection_participants },
    { label: "5. Interações Comerciais", value: commercialActionsCount },
  ];
  const conversionFunnel = buildFunnelChart(fullConversionFunnelRows, {});

  const activityChart = buildTrendChart(
    daily,
    [
      { name: "Sessões no webapp", data: daily.map((row) => toNumber(row.socket_sessions)) },
      { name: "Páginas acessadas", data: daily.map((row) => toNumber(row.route_accesses)) },
    ],
    CHART_PALETTE,
  );

  return (
    <>
      <Row className="mx-0">
        <KpiCard
          title="Público do evento"
          value={formatKpiNumber(overview.eligible_participants)}
          subtitle="Participantes cadastrados"
          icon={faUsers}
        />
        <KpiCard
          title="Presença (Check-in)"
          value={formatKpiNumber(overview.checked_in_participants)}
          subtitle={checkinUsageSubtitle(overview.checked_in_participants, overview.app_adoption_among_attendees)}
          icon={faQrcode}
        />
        <KpiCard
          title="Adoção do Webapp"
          value={formatKpiNumber(overview.activated_participants)}
          subtitle={`${formatPercentWithBase(overview.activation_rate, overview.eligible_participants)} dos cadastrados`}
          icon={faWifi}
          tooltip={KPI_TOOLTIPS.users}
        />
        <KpiCard
          title="Networking Ativo"
          value={formatKpiNumber(overview.connection_participants)}
          subtitle="Conexão aceita, QR ou conversa"
          icon={faHandshake}
        />
        <KpiCard
          title="Interações Comerciais"
          value={formatKpiNumber(commercialActionsCount)}
          subtitle="Cliques e leads gerados"
          icon={faChartLine}
        />
      </Row>
      <Row className="mx-0 pb-3">
        <ToggleableChartCard
          title="Funil Unificado de Conversão"
          tooltip="Jornada ponta a ponta: do cadastro à presença, adesão ao webapp, networking e conversão comercial."
          tableData={fullConversionFunnelRows}
          tableColumns={[
            { Header: "Estágio da Jornada", accessor: "label" },
            { Header: "Participantes / Volume", accessor: "value", Cell: numberCell },
          ]}
          tableTitle="Funil Unificado de Conversão"
        >
          {toNumber(overview.eligible_participants) > 0 ? (
            <Chart options={conversionFunnel.options} series={conversionFunnel.series} type="bar" height="330" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>
        <ToggleableChartCard
          title="Ritmo de Atividade Diária"
          tooltip="Evolução diária de sessões ativas e páginas acessadas ao longo do período selecionado."
          tableData={daily}
          tableColumns={[
            { Header: "Data", accessor: "metric_date", Cell: dateCell },
            { Header: "Sessões", accessor: "socket_sessions", Cell: numberCell },
            { Header: "Páginas acessadas", accessor: "route_accesses", Cell: numberCell },
          ]}
          tableTitle="Atividade Diária"
        >
          {hasChartData(daily, ["socket_sessions", "route_accesses"]) ? (
            <Chart options={activityChart.options} series={activityChart.series} type="line" height="330" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>
      </Row>
    </>
  );
}

// -----------------------------------------------------------------------------
// SECTION 2: PÚBLICO & ADESÃO
// -----------------------------------------------------------------------------
function AudienceSection({ data }) {
  const environmentSummary = data?.environment_summary || {};
  const summary = data?.summary || {};
  const daily = safeRows(data?.daily);
  const hasAudienceActivity = [
    summary.activated_participants,
    summary.checked_in_participants,
    summary.guest_devices,
    summary.guest_sessions,
  ].some((value) => toNumber(value) > 0);

  const dailyChart = buildTrendChart(
    daily,
    [
      { name: "Registrados", data: daily.map((row) => toNumber(row.registered_users)) },
      { name: "Visitantes estimados", data: daily.map((row) => toNumber(row.guest_devices)) },
      { name: "Sessões de visitantes", data: daily.map((row) => toNumber(row.guest_sessions)) },
    ],
    CHART_PALETTE,
  );

  const typeChart = buildDonutChart(data?.type_distribution, {
    labelGetter: (row) => labelUnknown(row.type_label, "Sem tipo"),
    valueGetter: (row) => row.users,
  });

  const retentionRows = safeRows(data?.retention)
    .map((row) => ({ ...row, label: `${formatNumber(row.active_days)} dia${toNumber(row.active_days) === 1 ? "" : "s"}` }))
    .sort((a, b) => toNumber(a.active_days) - toNumber(b.active_days));

  const retentionChart = buildBarChart(retentionRows, {
    labelGetter: (row) => row.label,
    valueGetter: (row) => row.users,
    name: "Registrados",
    horizontal: false,
    colors: CHART_PALETTE,
  });

  const environmentRows = buildAudienceEnvironmentRows(data);
  const environmentChart = buildBarChart(environmentRows, {
    labelGetter: (row) => row.label,
    valueGetter: (row) => row.total,
    name: "Aparelhos / Sessões",
    colors: CHART_PALETTE,
    colorGetter: (row, index) => row.permission === "granted"
      ? CHART_STATUS_COLORS.success
      : row.permission === "denied"
        ? CHART_STATUS_COLORS.danger
        : CHART_PALETTE[index % CHART_PALETTE.length],
    limit: 8,
  });

  const activeStandaloneTotal = toNumber(environmentSummary.active_standalone_registered_users)
    + toNumber(environmentSummary.active_standalone_guest_sessions);

  if (!hasAudienceActivity) {
    return <EmptySectionState text="Nenhum participante ou visitante usou o webapp neste período." />;
  }

  return (
    <>
      <Row className="mx-0">
        <KpiCard
          title="Acesso ao webapp"
          value={formatKpiNumber(summary.activated_participants)}
          subtitle={`${formatPercentWithBase(summary.activation_rate, summary.eligible_participants)} dos participantes`}
          icon={faWifi}
          tooltip={KPI_TOOLTIPS.users}
        />
        <KpiCard
          title="Participantes recorrentes"
          value={formatKpiNumber(summary.returning_participants)}
          subtitle="Ativos em 2 ou mais dias"
          icon={faRefresh}
        />
        <KpiCard
          title="Visitantes estimados"
          value={formatKpiNumber(summary.guest_devices)}
          subtitle={`${formatNumber(summary.guest_sessions)} sessões de visitantes`}
          icon={faUsers}
          tooltip={KPI_TOOLTIPS.estimatedVisitors}
        />
        <KpiCard
          title="Acesso via PWA / App"
          value={formatKpiNumber(activeStandaloneTotal)}
          subtitle={`${formatNumber(environmentSummary.active_standalone_registered_users)} participantes registrados`}
          icon={faBell}
        />
      </Row>

      <Row className="mx-0 pb-3">
        <ToggleableChartCard
          title="Público por dia"
          tooltip="Registrados, visitantes estimados e sessões de visitantes por dia do período."
          tableData={daily}
          tableColumns={[
            { Header: "Data", accessor: "metric_date", Cell: dateCell },
            { Header: "Registrados", accessor: "registered_users", Cell: numberCell },
            { Header: "Visitantes estimados", accessor: "guest_devices", Cell: numberCell },
            { Header: "Sessões visitantes", accessor: "guest_sessions", Cell: numberCell },
          ]}
          tableTitle="Público por dia"
        >
          {hasChartData(daily, ["registered_users", "guest_devices", "guest_sessions"]) ? (
            <Chart options={dailyChart.options} series={dailyChart.series} type="line" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Tipos de participante"
          tooltip="Distribuição dos participantes cadastrados que acessaram o webapp no período."
          tableData={data?.type_distribution}
          tableColumns={columnSets.typeDistribution}
          tableTitle="Tipos de participante"
        >
          {typeChart.rows.length > 0 ? (
            <Chart options={typeChart.options} series={typeChart.series} type="donut" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Retenção (Dias ativos)"
          tooltip="Quantidade de participantes agrupados pelo número de dias com uso no período."
          tableData={retentionRows}
          tableColumns={columnSets.retention}
          tableTitle="Retenção de participantes"
        >
          {retentionChart.rows.length > 0 ? (
            <Chart options={retentionChart.options} series={retentionChart.series} type="bar" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Push e Modo de Abertura"
          tooltip="Estado observado de permissão de Push e modo de execução (Navegador vs App instalado)."
          tableData={data?.environment_distribution}
          tableColumns={columnSets.environment}
          tableTitle="Ambiente e permissões"
        >
          {environmentChart.rows.length > 0 ? (
            <Chart options={environmentChart.options} series={environmentChart.series} type="bar" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>
      </Row>

    </>
  );
}

// -----------------------------------------------------------------------------
// SECTION 3: JORNADA & NAVEGAÇÃO (AQUISIÇÃO + NAVEGAÇÃO)
// -----------------------------------------------------------------------------
function JourneySection({ acquisitionData = {}, navigationData = {} }) {
  const [routeMode, setRouteMode] = useState("parent");

  const routes = routeMode === "parent" ? navigationData?.parent_routes : navigationData?.top_routes;

  const entrySourceChart = buildDonutChart(acquisitionData?.entry_sources, {
    labelGetter: (row) => labelEntrySource(row.entry_source),
    valueGetter: (row) => row.socket_sessions,
  });

  const campaignChart = buildBarChart(acquisitionData?.campaigns, {
    labelGetter: labelCampaign,
    valueGetter: (row) => row.socket_sessions,
    name: "Sessões",
    colors: CHART_PALETTE,
    limit: 10,
  });

  const topPagesChart = buildBarChart(navigationData?.parent_routes, {
    labelGetter: (row) => labelRoutePath(row.route_path),
    valueGetter: (row) => row.access_count,
    name: "Acessos",
    colors: CHART_PALETTE,
    limit: 10,
  });

  const routeDurationChart = buildBarChart(navigationData?.route_duration, {
    labelGetter: (row) => labelRoutePath(row.route_path),
    valueGetter: (row) => row.duration_seconds,
    name: "Tempo visível",
    colors: CHART_PALETTE,
    valueFormatter: formatSeconds,
    limit: 10,
  });

  const sourceConversionChart = buildGroupedBarChart(
    safeRows(acquisitionData?.source_conversion).slice(0, 8),
    [
      { name: "Ativação", valueGetter: (row) => row.activation_rate },
      { name: "Networking", valueGetter: (row) => row.networking_conversion_rate },
      { name: "Patrocinadores", valueGetter: (row) => row.sponsor_conversion_rate },
    ],
    {
      categoryGetter: (row) => labelEntrySource(row.entry_source),
      colors: CHART_PALETTE,
      valueFormatter: formatPercentValue,
    },
  );

  return (
    <>
      <Row className="mx-0 pb-3">
        <ToggleableChartCard
          title="Origem das sessões"
          tooltip="Primeira origem observada quando a sessão foi iniciada (Direto, UTM, QR, PWA)."
          tableData={acquisitionData?.entry_sources}
          tableColumns={columnSets.entrySources}
          tableTitle="Origem das sessões"
        >
          {entrySourceChart.rows.length > 0 ? (
            <Chart options={entrySourceChart.options} series={entrySourceChart.series} type="donut" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Campanhas UTM"
          tooltip="Sessões iniciadas a partir de parâmetros de campanha UTM na URL."
          tableData={acquisitionData?.campaigns}
          tableColumns={columnSets.campaigns}
          tableTitle="Campanhas UTM"
        >
          {campaignChart.rows.length > 0 ? (
            <Chart options={campaignChart.options} series={campaignChart.series} type="bar" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Páginas mais acessadas"
          tooltip="Páginas principais navegadas pelos usuários dentro do webapp."
          tableData={routes}
          tableColumns={columnSets.routes}
          tableTitle="Páginas acessadas"
          extraHeader={
            <Form.Select size="sm" value={routeMode} onChange={(e) => setRouteMode(e.target.value)} style={{ width: "auto" }}>
              <option value="parent">Por página principal</option>
              <option value="full">URL completa</option>
            </Form.Select>
          }
        >
          {topPagesChart.rows.length > 0 ? (
            <Chart options={topPagesChart.options} series={topPagesChart.series} type="bar" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Tempo de permanência por página"
          tooltip="Soma do tempo visível registrado quando o usuário sai de cada rota. Rotas com parâmetros, como :streaming_id, agrupam os registros e não identificam cada transmissão."
          tableData={navigationData?.route_duration}
          tableColumns={columnSets.routeDuration}
          tableTitle="Tempo por página"
        >
          {routeDurationChart.rows.length > 0 ? (
            <Chart options={routeDurationChart.options} series={routeDurationChart.series} type="bar" height="300" />
          ) : (
            <ChartEmpty />
          )}
          {safeRows(navigationData?.route_duration).some((row) => /\/:[^/]+/.test(row.route_path || "")) && (
            <p className="text-muted small mb-0">Rotas com “:parâmetro” agrupam páginas. Os IDs específicos não estão disponíveis nesses registros.</p>
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Conversão por canal de entrada"
          tooltip="Taxas de ativação, networking e patrocínio calculadas sobre a coorte de entrada."
          tableData={acquisitionData?.source_conversion}
          tableColumns={columnSets.sourceConversion}
          tableTitle="Conversão por origem"
          xl={12}
        >
          {hasChartData(sourceConversionChart.rows, ["activation_rate", "networking_conversion_rate", "sponsor_conversion_rate"]) ? (
            <Chart options={sourceConversionChart.options} series={sourceConversionChart.series} type="bar" height="330" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>
      </Row>

    </>
  );
}

// -----------------------------------------------------------------------------
// SECTION 4: CONTEÚDO & COMERCIAL
// -----------------------------------------------------------------------------
function ContentCommercialSection({ contentData = {}, commercialData = {} }) {
  const contentSummary = contentData?.summary || {};
  const commOverview = commercialData?.overview || {};
  const daily = safeRows(contentData?.daily);

  const scheduleChart = buildGroupedBarChart(
    safeRows(contentData?.schedule_preview),
    [
      { name: "Aberturas", valueGetter: (row) => row.detail_opens },
      { name: "Interesses", valueGetter: (row) => row.period_interests },
      { name: "Confirmações", valueGetter: (row) => row.period_confirmations },
    ],
    {
      categoryGetter: (row) => labelUnknown(row.title, "Agenda"),
      colors: [CHART_PALETTE[0], CHART_PALETTE[1], CHART_STATUS_COLORS.success],
      horizontal: true,
      limit: 10,
    },
  );

  const sponsorChart = buildGroupedBarChart(
    safeRows(commercialData?.sponsor_clicks),
    [
      { name: "Impressões", valueGetter: (row) => row.impression_count },
      { name: "Cliques", valueGetter: (row) => row.click_count },
    ],
    {
      categoryGetter: (row) => labelUnknown(row.sponsor_name, "Patrocinador"),
      colors: CHART_PALETTE,
      horizontal: true,
      limit: 10,
    },
  );

  const qrRows = safeRows(commercialData?.qr_summary);
  const qrTypeChart = buildDonutChart(qrRows, {
    labelGetter: (row) => labelQrEventName(row.event_name),
    valueGetter: (row) => row.scan_count,
  });

  const dailyTrend = buildTrendChart(
    daily,
    [
      { name: "Agenda", data: daily.map((row) => toNumber(row.schedule_opens)) },
      { name: "Palestrantes", data: daily.map((row) => toNumber(row.speaker_opens)) },
      { name: "Expositores", data: daily.map((row) => toNumber(row.exhibitor_opens)) },
    ],
    CHART_PALETTE,
  );

  return (
    <>
      <Row className="mx-0">
        <KpiCard
          title="Acessos à agenda"
          value={formatKpiNumber(contentSummary.schedule_detail_opens)}
          subtitle={`${formatNumber(contentSummary.period_schedule_interests)} interesses registrados`}
          icon={faCalendar}
        />
        <KpiCard
          title="Visualizações de patrocinadores"
          value={formatKpiNumber(commOverview.sponsor_impressions)}
          subtitle={`${formatNumber(commOverview.sponsor_clicks)} cliques gerados`}
          icon={faChartLine}
        />
        <KpiCard
          title="Leads de expositores"
          value={formatKpiNumber(commOverview.unique_exhibitor_leads)}
          subtitle={`${formatNumber(commOverview.lead_collections)} coletas realizadas`}
          icon={faUsers}
        />
        <KpiCard
          title="Ações por QR Code"
          value={formatKpiNumber(commOverview.qr_actions)}
          subtitle={`${formatNumber(commOverview.qr_registered_users)} usuários únicos`}
          icon={faQrcode}
        />
      </Row>

      <Row className="mx-0 pb-3">
        <ToggleableChartCard
          title="Agenda e Atrações em destaque"
          tooltip="Atividades com maior engajamento, interesses e confirmações de presença."
          tableData={contentData?.schedule_preview}
          tableColumns={columnSets.contentSchedule}
          tableTitle="Agenda e atrações"
        >
          {hasChartData(scheduleChart.rows, ["detail_opens", "period_interests", "period_confirmations"]) ? (
            <Chart options={scheduleChart.options} series={scheduleChart.series} type="bar" height="320" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Engajamento com Patrocinadores"
          tooltip="Volume de impressões e cliques externos nos links de patrocinadores."
          tableData={commercialData?.sponsor_clicks}
          tableColumns={columnSets.sponsorClicks}
          tableTitle="Patrocinadores"
        >
          {sponsorChart.rows.length > 0 ? (
            <Chart options={sponsorChart.options} series={sponsorChart.series} type="bar" height="320" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Interações por QR Code"
          tooltip="Scans válidos realizados pelos participantes (contatos, leads, estandes)."
          tableData={commercialData?.qr_summary}
          tableColumns={columnSets.qrSummary}
          tableTitle="Ações por QR code"
        >
          {qrTypeChart.rows.length > 0 ? (
            <Chart options={qrTypeChart.options} series={qrTypeChart.series} type="donut" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Evolução diária de conteúdo"
          tooltip="Acessos à agenda, palestrantes e expositores ao longo dos dias do evento."
          tableData={daily}
          tableColumns={[
            { Header: "Data", accessor: "metric_date", Cell: dateCell },
            { Header: "Agenda", accessor: "schedule_opens", Cell: numberCell },
            { Header: "Palestrantes", accessor: "speaker_opens", Cell: numberCell },
            { Header: "Expositores", accessor: "exhibitor_opens", Cell: numberCell },
          ]}
          tableTitle="Evolução de conteúdo"
        >
          {hasChartData(daily, ["schedule_opens", "speaker_opens", "exhibitor_opens"]) ? (
            <Chart options={dailyTrend.options} series={dailyTrend.series} type="line" height="300" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        {safeRows(commercialData?.leads_by_exhibitor).length > 0 && (
          <Col xs={12} className="mb-3">
            <DashboardTable
              title="Leads coletados por expositor"
              description="Contatos comerciais e trocas realizadas nos estandes dos expositores."
              columns={columnSets.leadsByExhibitor}
              data={commercialData?.leads_by_exhibitor}
            />
          </Col>
        )}
      </Row>
    </>
  );
}

// -----------------------------------------------------------------------------
// SECTION 5: NETWORKING & MENSAGERIA
// -----------------------------------------------------------------------------
function NetworkingCommunicationsSection({ networkingData = {}, communicationsData = {} }) {
  const netSummary = networkingData?.summary || {};
  const commSummary = communicationsData?.summary || {};
  const netDaily = safeRows(networkingData?.daily);

  const invitationFunnelRows = [
    { label: "Convites enviados", value: netSummary.requests_sent },
    { label: "Convites respondidos", value: toNumber(netSummary.accepted_actions) + toNumber(netSummary.rejected_actions) },
    { label: "Convites aceitos", value: netSummary.accepted_actions },
  ];
  const invitationFunnel = buildFunnelChart(invitationFunnelRows, {
    colors: [CHART_PALETTE[0], CHART_PALETTE[1], CHART_STATUS_COLORS.success],
  });
  const activityHeatmap = buildHeatmapChart(networkingData?.activity_heatmap);

  const commTrend = buildTrendChart(
    safeRows(communicationsData?.daily),
    [
      { name: "Notificações", data: safeRows(communicationsData?.daily).map((r) => toNumber(r.notifications_created)) },
      { name: "Web Push aceitos", data: safeRows(communicationsData?.daily).map((r) => toNumber(r.provider_accepted)) },
      { name: "E-mails entregues", data: safeRows(communicationsData?.daily).map((r) => toNumber(r.email_delivered)) },
    ],
    [CHART_PALETTE[0], CHART_STATUS_COLORS.success, CHART_STATUS_COLORS.successDark],
  );

  return (
    <>
      <Row className="mx-0">
        <KpiCard
          title="Participantes no networking"
          value={formatKpiNumber(netSummary.networking_users)}
          subtitle={`${formatPercentWithBase(netSummary.connection_coverage, netSummary.activated_participants)} dos ativos`}
          icon={faUsers}
        />
        <KpiCard
          title="Conexões realizadas"
          value={formatKpiNumber(netSummary.meaningful_connections)}
          subtitle="Pares únicos com resultado"
          icon={faHandshake}
        />
        <KpiCard
          title="Conversas ativas"
          value={formatKpiNumber(netSummary.two_way_conversations)}
          subtitle="Com troca de mensagens"
          icon={faEnvelope}
        />
        <KpiCard
          title="Web Push aceitos"
          value={formatKpiNumber(commSummary.provider_accepted)}
          subtitle={`${formatNumber(commSummary.provider_accepted)} de ${formatNumber(commSummary.push_attempts)}`}
          icon={faBell}
        />
        <KpiCard
          title="E-mails entregues"
          value={formatKpiNumber(commSummary.email_delivered)}
          subtitle={`${formatNumber(commSummary.email_opened)} abertos`}
          icon={faEnvelope}
        />
      </Row>

      <Row className="mx-0 pb-3">
        <ToggleableChartCard
          title="Funil de convites de networking"
          tooltip="Fluxo de convites enviados, respondidos e aceitos entre participantes."
          tableData={invitationFunnelRows}
          tableColumns={[
            { Header: "Etapa", accessor: "label" },
            { Header: "Volume", accessor: "value", Cell: numberCell },
          ]}
          tableTitle="Funil de convites"
        >
          {toNumber(netSummary.requests_sent) > 0 ? (
            <Chart options={invitationFunnel.options} series={invitationFunnel.series} type="bar" height="320" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <ToggleableChartCard
          title="Atividade de mensageria por dia"
          tooltip="Notificações, Web Push aceitos e e-mails entregues ao longo do período."
          tableData={communicationsData?.daily}
          tableColumns={[
            { Header: "Data", accessor: "metric_date", Cell: dateCell },
            { Header: "Notificações", accessor: "notifications_created", Cell: numberCell },
            { Header: "Web Push aceitos", accessor: "provider_accepted", Cell: numberCell },
            { Header: "E-mails entregues", accessor: "email_delivered", Cell: numberCell },
          ]}
          tableTitle="Mensageria diária"
        >
          {hasChartData(safeRows(communicationsData?.daily), ["notifications_created", "provider_accepted", "email_delivered"]) ? (
            <Chart options={commTrend.options} series={commTrend.series} type="line" height="320" />
          ) : (
            <ChartEmpty />
          )}
        </ToggleableChartCard>

        <Col xs={12} className="mb-3">
          <Card>
            <Card.Body>
              <TitleWithHelp className="mb-3" tooltip="Concentração de ações de relacionamento, aberturas de perfil e mensagens diretas por dia da semana e hora.">
                Mapa de calor: Horários de maior interação
              </TitleWithHelp>
              {activityHeatmap.rows.length > 0 ? (
                <Chart options={activityHeatmap.options} series={activityHeatmap.series} type="heatmap" height="350" />
              ) : (
                <ChartEmpty />
              )}
            </Card.Body>
          </Card>
        </Col>

        {safeRows(communicationsData?.notification_types).length > 0 && (
          <Col xs={12} className="mb-3">
            <DashboardTable
              title="Notificações criadas por tipo"
              columns={columnSets.notificationTypes}
              data={communicationsData?.notification_types}
            />
          </Col>
        )}
      </Row>

    </>
  );
}

// -----------------------------------------------------------------------------
// RENDER SECTION ROUTER
// -----------------------------------------------------------------------------
function renderSection(section, data) {
  if (section === "audience") {
    return <AudienceSection data={data?.audience || data} />;
  }
  if (section === "journey") {
    return <JourneySection acquisitionData={data?.acquisition || {}} navigationData={data?.navigation || {}} />;
  }
  if (section === "business") {
    return <ContentCommercialSection contentData={data?.content || {}} commercialData={data?.commercial || {}} />;
  }
  if (section === "connections") {
    return <NetworkingCommunicationsSection networkingData={data?.networking || {}} communicationsData={data?.communications || {}} />;
  }
  return <UnifiedOverviewSection data={data?.overview || data} commercialData={data?.commercial || {}} />;
}

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------
export const EventAnalyticsDashboard = ({ dataTable, handleDelete, canReset, canRunUsageTest = false, refreshTrigger, onLoadingChange }) => {
  const notyf = useContext(NotyfContext);
  const eventId = dataTable?.event_id || dataTable?.evento_id;
  const initialQueryState = useMemo(() => readAnalyticsQueryState(dataTable), [dataTable]);
  const [preset, setPreset] = useState(initialQueryState.preset);
  const [draftRange, setDraftRange] = useState(initialQueryState.range);
  const [appliedRange, setAppliedRange] = useState(initialQueryState.range);
  const [activeSection, setActiveSection] = useState(initialQueryState.section);
  const [sectionPayloads, setSectionPayloads] = useState({});
  const [sectionLoading, setSectionLoading] = useState({});
  const [sectionErrors, setSectionErrors] = useState({});
  const [analyticsEpoch, setAnalyticsEpoch] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [downloadingDataset, setDownloadingDataset] = useState(null);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [showUsageTest, setShowUsageTest] = useState(false);
  const [, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);
  const minimumAnalyticsDate = analyticsEpoch?.eventId === eventId ? analyticsEpoch.minimumDate : null;

  const activeSectionDef = DASHBOARD_SECTIONS.find((s) => s.key === activeSection) || DASHBOARD_SECTIONS[0];
  const requiredApiSections = activeSectionDef.apiSections || [activeSection];
  const currentRangeCacheKey = JSON.stringify([eventId, getRangeKey(appliedRange), dataTable?.timezone, refreshTrigger, retryKey]);

  useEffect(() => {
    if (!eventId || !appliedRange?.startDate || !appliedRange?.endDate) {
      onLoadingChange?.("analytics", false);
      return;
    }

    const cacheKey = currentRangeCacheKey;
    const requestId = ++requestIdRef.current;
    const source = axios.CancelToken.source();
    let active = true;
    const synchronizeAnalyticsEpoch = (range) => {
      const minimumDate = parseUtcDate(range?.analytics_reset_at);
      const timezone = range?.timezone || dataTable?.timezone || DASHBOARD_TIMEZONE;
      setAnalyticsEpoch((previous) => previous?.eventId === eventId
        && previous.minimumDate?.getTime() === minimumDate?.getTime() && previous.timezone === timezone
        ? previous : { eventId, minimumDate, timezone });
      const availableRange = getAvailableAnalyticsRange(appliedRange, minimumDate);
      if (isSameRange(availableRange, appliedRange)) return false;
      setDraftRange(availableRange);
      setAppliedRange(availableRange);
      setSectionPayloads({});
      setSectionErrors({});
      return true;
    };

    setSectionLoading({ [cacheKey]: true });
    onLoadingChange?.("analytics", true);
    setSectionErrors({});

    Promise.allSettled(
      DASHBOARD_API_SECTIONS.map((sec) =>
        axios.get(`/api/event/plataformaEventos/analytics-dashboard/${eventId}`, {
          params: {
            section: sec,
            start_date: appliedRange.startDate.toISOString(),
            end_date: appliedRange.endDate.toISOString(),
          },
          cancelToken: source.token,
        }),
      ),
    )
      .then((results) => {
        if (!active || requestId !== requestIdRef.current) return;
        const resetFailure = results.find((result) => result.status === "rejected" && result.reason?.response?.data?.code === "RANGE_PRECEDES_RESET");
        const serverRange = resetFailure?.reason.response.data || results.find((result) => result.status === "fulfilled")?.value?.data?.range;
        if (serverRange && synchronizeAnalyticsEpoch(serverRange)) return;
        const payloads = {};
        const errors = {};
        results.forEach((result, index) => {
          const sectionKey = `${DASHBOARD_API_SECTIONS[index]}:${cacheKey}`;
          if (result.status === "fulfilled") {
            payloads[sectionKey] = { cacheKey, payload: result.value.data || EMPTY_PAYLOAD };
          } else if (!axios.isCancel(result.reason)) {
            errors[sectionKey] = formatAnalyticsErrorMessage(result.reason, "Erro ao carregar dados desta seção.");
          }
        });
        setSectionPayloads(payloads);
        setSectionErrors(errors);
        const message = [...new Set(Object.values(errors).filter(Boolean))].join(" ");
        if (message) notyf.open({ type: "danger", message, ripple: true, dismissible: true });
      })
      .finally(() => {
        if (!active || requestId !== requestIdRef.current) return;
        setSectionLoading({ [cacheKey]: false });
        onLoadingChange?.("analytics", false);
      });

    return () => { active = false; source.cancel("analytics-range-change"); };
  }, [appliedRange, currentRangeCacheKey, dataTable?.timezone, eventId, notyf, onLoadingChange]);

  const handlePreset = (nextPreset) => {
    const nextRange = getAvailableAnalyticsRange(getPresetRange(nextPreset, dataTable), minimumAnalyticsDate);
    setPreset(nextPreset);
    setDraftRange(nextRange);
    setAppliedRange(nextRange);
    setSectionPayloads({});
    setSectionErrors({});
  };

  const applyDateSearch = () => {
    const normalizedDraftRange = normalizeDashboardRange(draftRange, timezoneLabel);
    if (minimumAnalyticsDate && normalizedDraftRange.startDate && normalizedDraftRange.startDate < minimumAnalyticsDate
      && formatPickerDate(normalizedDraftRange.startDate, timezoneLabel) === formatPickerDate(minimumAnalyticsDate, timezoneLabel)) {
      normalizedDraftRange.startDate = minimumAnalyticsDate;
    }
    const validationMessage = getRangeValidationMessage(normalizedDraftRange, minimumAnalyticsDate, timezoneLabel);
    if (validationMessage) {
      notyf.open({ type: "danger", message: validationMessage, ripple: true, dismissible: true });
      return;
    }
    if (isSameRange(normalizedDraftRange, appliedRange)) return;
    setPreset("custom");
    setAppliedRange(normalizedDraftRange);
    setSectionPayloads({});
    setSectionErrors({});
  };

  const retrySection = () => {
    setSectionPayloads({});
    setRetryKey((v) => v + 1);
  };

  const currentSectionData = useMemo(() => {
    const combined = {};
    requiredApiSections.forEach((sec) => {
      const entry = sectionPayloads[`${sec}:${currentRangeCacheKey}`];
      if (entry?.payload?.data) {
        combined[sec] = entry.payload.data;
      }
    });
    return combined;
  }, [currentRangeCacheKey, requiredApiSections, sectionPayloads]);

  const primaryFirstEntry = sectionPayloads[`${requiredApiSections[0]}:${currentRangeCacheKey}`];
  const currentPayload = primaryFirstEntry?.payload || {};
  const isSectionFullyLoaded = requiredApiSections.every((sec) => Boolean(sectionPayloads[`${sec}:${currentRangeCacheKey}`]));
  const currentLoading = sectionLoading[currentRangeCacheKey] === true && !isSectionFullyLoaded;
  const currentError = [...new Set(requiredApiSections.map((section) => sectionErrors[`${section}:${currentRangeCacheKey}`]).filter(Boolean))].join(" ");
  const serverRange = currentPayload.range;
  const timezoneLabel = serverRange?.timezone || (analyticsEpoch?.eventId === eventId && analyticsEpoch.timezone) || dataTable?.timezone || DASHBOARD_TIMEZONE;
  const isAvailableAnalyticsDay = (date) => !minimumAnalyticsDate
    || date.format("YYYY-MM-DD") >= formatInTimeZone(minimumAnalyticsDate, timezoneLabel, "yyyy-MM-dd");
  const hasReportActions = Boolean(canRunUsageTest || (canReset && handleDelete));

  useEffect(() => {
    if (typeof window === "undefined" || !window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("analytics_section", activeSection);
      url.searchParams.set("analytics_preset", preset);
      if (appliedRange?.startDate && appliedRange?.endDate) {
        url.searchParams.set("analytics_start", appliedRange.startDate.toISOString());
        url.searchParams.set("analytics_end", appliedRange.endDate.toISOString());
      }
      window.history.replaceState(null, "", `${url.pathname}${url.search}`);
    } catch (_) {}
  }, [activeSection, appliedRange, preset]);

  const downloadAnalyticsCsv = async (dataset) => {
    if (!eventId || !appliedRange?.startDate || !appliedRange?.endDate || downloadingDataset) return;
    setDownloadingDataset(dataset);
    try {
      const response = await axios.get(
        `/api/event/plataformaEventos/analytics-dashboard/${eventId}/export/${dataset}.csv`,
        {
          params: {
            start_date: appliedRange.startDate.toISOString(),
            end_date: appliedRange.endDate.toISOString(),
          },
          responseType: "blob",
        },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `analytics-${eventId}-${dataset}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = formatAnalyticsErrorMessage(error, "Não foi possível baixar o CSV solicitado.");
      if (message) {
        notyf.open({ type: "danger", message, ripple: true, dismissible: true });
      }
    } finally {
      setDownloadingDataset(null);
    }
  };

  const exportConsolidatedXlsx = async () => {
    if (!eventId || !appliedRange?.startDate || !appliedRange?.endDate || isExportingAll) return;
    setIsExportingAll(true);
    try {
      const responses = await Promise.all(
        DASHBOARD_API_SECTIONS.map((sec) => {
          const cached = sectionPayloads[`${sec}:${currentRangeCacheKey}`];
          if (cached?.payload?.data) return Promise.resolve({ section: sec, data: cached.payload.data });
          return axios.get(`/api/event/plataformaEventos/analytics-dashboard/${eventId}`, {
            params: {
              section: sec,
              start_date: appliedRange.startDate.toISOString(),
              end_date: appliedRange.endDate.toISOString(),
            },
          }).then((res) => ({ section: sec, data: res.data?.data || {} }))
          .catch(() => ({ section: sec, data: {} }));
        }),
      );

      const sectionMap = {};
      responses.forEach((r) => { sectionMap[r.section] = r.data; });

      const workbook = XLSX.utils.book_new();

      const appendSheetSafe = (rows, sheetName) => {
        if (!Array.isArray(rows) || rows.length === 0) return;
        const cleanRows = rows.map((row) => {
          const obj = {};
          Object.keys(row).forEach((k) => {
            if (row[k] !== null && typeof row[k] === "object") {
              obj[k] = JSON.stringify(row[k]);
            } else {
              obj[k] = row[k];
            }
          });
          return obj;
        });
        const worksheet = XLSX.utils.json_to_sheet(cleanRows);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31));
      };

      if (sectionMap.overview?.overview) {
        appendSheetSafe([sectionMap.overview.overview], "Resumo_Executivo");
      }
      appendSheetSafe(sectionMap.audience?.users, "Participantes");
      appendSheetSafe(sectionMap.audience?.type_distribution, "Tipos_Participante");
      appendSheetSafe(sectionMap.acquisition?.entry_sources, "Canais_Entrada");
      appendSheetSafe(sectionMap.acquisition?.campaigns, "Campanhas_UTM");
      appendSheetSafe(sectionMap.navigation?.parent_routes, "Paginas_Mais_Acessadas");
      appendSheetSafe(sectionMap.content?.schedule_preview, "Agenda_Conteudo");
      appendSheetSafe(sectionMap.commercial?.sponsor_clicks, "Patrocinadores");
      appendSheetSafe(sectionMap.commercial?.leads_by_exhibitor, "Leads_Expositores");
      appendSheetSafe(sectionMap.commercial?.qr_summary, "Acoes_QR");
      appendSheetSafe(sectionMap.networking?.pair_preview, "Networking_Pares");
      appendSheetSafe(sectionMap.communications?.notification_types, "Notificacoes");

      if (workbook.SheetNames.length === 0) {
        const emptySheet = XLSX.utils.json_to_sheet([{ Mensagem: "Nenhum dado analítico registrado para o período selecionado." }]);
        XLSX.utils.book_append_sheet(workbook, emptySheet, "Sem_Dados");
      }

      XLSX.writeFile(workbook, `relatorio-analitico-consolidado-${eventId}.xlsx`);
      notyf.open({ type: "success", message: "Relatório consolidado XLSX gerado com sucesso!", ripple: true, dismissible: true });
    } catch (error) {
      const message = formatAnalyticsErrorMessage(error, "Erro ao gerar o relatório consolidado XLSX.");
      if (message) {
        notyf.open({ type: "danger", message, ripple: true, dismissible: true });
      }
    } finally {
      setIsExportingAll(false);
    }
  };

  return (
    <div className="analytics-dashboard">
      <style>{`
        .analytics-kpi-title { color: #495057; font-weight: 600; }
        .analytics-kpi-value { font-variant-numeric: tabular-nums; }
        .analytics-section-summary { min-height: 1.25rem; }
        .analytics-filter-bar { margin-top: 1rem; padding-top: .75rem; }
        .analytics-dashboard .alert { padding: .5rem; }
        @media (prefers-reduced-motion: reduce) { .analytics-dashboard *, .analytics-dashboard *::before, .analytics-dashboard *::after { scroll-behavior: auto !important; transition: none !important; animation: none !important; } }
        @media print { .analytics-dashboard-actions, .analytics-filter-bar button, .analytics-filter-bar .rdtPicker { display: none !important; } .analytics-dashboard .card { break-inside: avoid; box-shadow: none !important; } .analytics-dashboard { color: #000; } }
      `}</style>
      <Form.Group as={Col} className="textHeaderAplicativo d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 className="card-title mb-0">Histórico de dados analíticos</h5>
        </div>
        <div className="analytics-dashboard-actions d-flex align-items-center gap-2">
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn-outline-primary" size="sm" title="Ações e exportação" aria-label="Ações e exportação">
              <FontAwesomeIcon icon={isExportingAll ? faRefresh : faCog} spin={isExportingAll} />
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              {canRunUsageTest && (
                <Dropdown.Item onClick={() => setShowUsageTest(true)}>
                  Testar captação de dados analíticos
                </Dropdown.Item>
              )}
              {canReset && handleDelete && (
                <Dropdown.Item
                  onClick={() => {
                    handleDelete(
                      {
                        platform: dataTable.platform,
                        evento_id: eventId,
                        type: "resetAnalytics",
                      },
                      "Um novo período de medição será iniciado. As interações do relatório passarão a considerar somente dados após esse corte; os eventos, contadores, sessões e tentativas de Web Push rastreados serão limpos. Dados operacionais e métricas identificadas como estado atual, como favoritos, serão preservados.",
                    );
                  }}
                >
                  Limpar dados analíticos rastreados do evento
                </Dropdown.Item>
              )}
              {hasReportActions && <Dropdown.Divider />}
              <Dropdown.Header>Exportar dados</Dropdown.Header>
              <Dropdown.Item
                onClick={() => downloadAnalyticsCsv(SECTION_CSV_DATASETS[activeSection] || "participant_activity")}
                disabled={downloadingDataset !== null}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2 text-muted" />
                {downloadingDataset === (SECTION_CSV_DATASETS[activeSection] || "participant_activity")
                  ? "Baixando CSV..."
                  : `Exportar CSV desta visão (${activeSectionDef.label})`}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => downloadAnalyticsCsv("participant_activity")}
                disabled={downloadingDataset !== null}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2 text-muted" />
                {downloadingDataset === "participant_activity"
                  ? "Baixando CSV..."
                  : "Exportar CSV: Atividade de participantes"}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => downloadAnalyticsCsv("routes")}
                disabled={downloadingDataset !== null}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2 text-muted" />
                {downloadingDataset === "routes"
                  ? "Baixando CSV..."
                  : "Exportar CSV: Páginas e rotas acessadas"}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => downloadAnalyticsCsv("sponsors")}
                disabled={downloadingDataset !== null}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2 text-muted" />
                {downloadingDataset === "sponsors"
                  ? "Baixando CSV..."
                  : "Exportar CSV: Patrocinadores"}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => downloadAnalyticsCsv("leads")}
                disabled={downloadingDataset !== null}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2 text-muted" />
                {downloadingDataset === "leads"
                  ? "Baixando CSV..."
                  : "Exportar CSV: Leads de expositores"}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => downloadAnalyticsCsv("networking_pairs")}
                disabled={downloadingDataset !== null}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2 text-muted" />
                {downloadingDataset === "networking_pairs"
                  ? "Baixando CSV..."
                  : "Exportar CSV: Pares de networking"}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={exportConsolidatedXlsx} disabled={isExportingAll}>
                <FontAwesomeIcon icon={faFileExcel} className="text-success me-2" />
                {isExportingAll ? "Gerando planilha consolidada..." : "Exportar relatório consolidado (XLSX multi-planilhas)"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Form.Group>

      <h6 className="text-muted mt-3 mx-2">
        Métricas de público, tráfego, interações, conteúdo e conversões captadas em tempo real ao longo do evento.
      </h6>

      <Row className="mb-3 mx-0 g-2">
        <Form.Group as={Col} xs={12} sm={6} md={3}>
          <Form.Label className="visually-hidden">Data inicial</Form.Label>
          <InputGroup className="w-100 flex-nowrap">
            <div className="flex-fill">
              <Datetime
                className="w-100"
                value={formatPickerDate(draftRange.startDate, timezoneLabel)}
                isValidDate={isAvailableAnalyticsDay}
                onChange={(date) => {
                  const nextDate = pickerDateToDashboardUtc(date, timezoneLabel);
                  if (nextDate) {
                    setPreset("custom");
                    setDraftRange((prev) => ({ ...prev, startDate: nextDate }));
                  }
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                inputProps={{
                  placeholder: "Data inicial",
                  className: "form-control",
                  "aria-label": "Data inicial",
                  style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
                }}
                locale="pt-br"
              />
            </div>
            <InputGroup.Text className="cursor-pointer" onClick={applyDateSearch}>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup>
        </Form.Group>

        <Form.Group as={Col} xs={12} sm={6} md={3}>
          <Form.Label className="visually-hidden">Data final</Form.Label>
          <InputGroup className="w-100 flex-nowrap">
            <div className="flex-fill">
              <Datetime
                className="w-100"
                value={formatPickerDate(draftRange.endDate, timezoneLabel)}
                isValidDate={isAvailableAnalyticsDay}
                onChange={(date) => {
                  const nextDate = pickerDateToDashboardUtc(date, timezoneLabel);
                  if (nextDate) {
                    setPreset("custom");
                    setDraftRange((prev) => ({ ...prev, endDate: nextDate }));
                  }
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                inputProps={{
                  placeholder: "Data final",
                  className: "form-control",
                  "aria-label": "Data final",
                  style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
                }}
                locale="pt-br"
              />
            </div>
            <InputGroup.Text className="cursor-pointer" onClick={applyDateSearch}>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup>
        </Form.Group>

        <Col xs={12} sm={6} md={6} className="d-flex align-items-center gap-2 flex-wrap">
          {DATE_PRESETS.map((item) => (
            <Button
              key={item.key}
              type="button"
              size="sm"
              variant={preset === item.key ? "primary" : "outline-secondary"}
              onClick={() => handlePreset(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </Col>
      </Row>

      <div className="analytics-filter-bar mb-3">
        <div className="d-flex flex-wrap gap-2 mb-1 mx-2" role="tablist" aria-label="Áreas do relatório">
          {DASHBOARD_SECTIONS.map((section) => (
            <Button
              key={section.key}
              type="button"
              size="sm"
              role="tab"
              aria-selected={activeSection === section.key}
              variant={activeSection === section.key ? "primary" : "outline-secondary"}
              onClick={() => setActiveSection(section.key)}
              title={SECTION_HELP[section.key]}
            >
              {section.label}
            </Button>
          ))}
        </div>
        <h6 className="analytics-section-summary text-muted mt-3 mx-2">
          {activeSectionDef.description}
        </h6>
      </div>

      {currentError && Object.keys(currentSectionData).length > 0 && (
        <Alert variant="danger" className="p-2">
          Falha ao atualizar dados: {currentError}
        </Alert>
      )}

      <AnalyticsTimezoneContext.Provider value={timezoneLabel}>
        <SectionShell loading={currentLoading} error={Object.keys(currentSectionData).length > 0 ? "" : currentError} onRetry={retrySection}>
          {renderSection(activeSection, currentSectionData)}
        </SectionShell>
      </AnalyticsTimezoneContext.Provider>

      <AnalyticsUsageLoadTestModal
        show={showUsageTest}
        onHide={() => setShowUsageTest(false)}
        onTerminal={() => setReloadKey((value) => value + 1)}
        context={{ eventId }}
      />
    </div>
  );
};

export const analyticsChartBuilders = { buildFunnelChart, buildHeatmapChart, formatAnalyticsErrorMessage };

export default EventAnalyticsDashboard;
